import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { usePermissions } from '../../hooks/usePermissions';
import { helpdeskTicketsAPI, helpdeskAgentsAPI, helpdeskCategoriesAPI, helpdeskAttachmentsAPI, helpdeskConfigAPI } from '../../lib/api';
import { AREAS, normalizeArea, getAreaColor } from '../../lib/helpdesk-constants';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { sanitizeHTML } from '../../components/RichPasteArea';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import {
  ArrowLeft,
  Ticket,
  Clock,
  CheckCircle2,
  AlertCircle,
  Pause,
  CalendarClock,
  User,
  Calendar,
  Tag,
  MessageSquare,
  Send,
  History,
  Building,
  UserCheck,
  Lock,
  Paperclip,
  Download,
  FileText,
  Image as ImageIcon,
  File,
  X,
  ZoomIn,
  Loader2,
  AlertTriangle,
  ThumbsUp,
  ThumbsDown,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatDateTimeMX, cn } from '../../lib/utils';

// Status configuration
const STATUS_CONFIG = {
  open: { label: 'Abierto', color: 'bg-blue-100 text-blue-700 border-blue-300', icon: AlertCircle },
  in_progress: { label: 'En proceso', color: 'bg-amber-100 text-amber-700 border-amber-300', icon: Clock },
  on_hold: { label: 'En espera', color: 'bg-slate-100 text-slate-700 border-slate-300', icon: Pause },
  scheduled: { label: 'Programado', color: 'bg-purple-100 text-purple-700 border-purple-300', icon: CalendarClock },
  closed: { label: 'Cerrado', color: 'bg-emerald-100 text-emerald-700 border-emerald-300', icon: CheckCircle2 },
};

// Priority configuration
const PRIORITY_CONFIG = {
  low: { label: 'Baja', color: 'bg-slate-100 text-slate-600' },
  medium: { label: 'Media', color: 'bg-amber-100 text-amber-700' },
  high: { label: 'Alta', color: 'bg-rose-100 text-rose-700' },
};

// Image Preview Component with modal zoom
const ImagePreview = ({ attachmentId, fileName, mimeType }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    loadImage();
  }, [attachmentId]);

  const loadImage = async () => {
    setLoading(true);
    setError(false);
    setUnavailable(false);
    try {
      // Fetch the image as blob using the download endpoint
      const response = await helpdeskAttachmentsAPI.download(attachmentId);
      const blob = new Blob([response.data], { type: mimeType });
      const objectUrl = URL.createObjectURL(blob);
      setImageUrl(objectUrl);
    } catch (err) {
      console.error('Error loading image preview:', err);
      if (err?.response?.status === 410) {
        setUnavailable(true);
      } else {
        setError(true);
      }
    } finally {
      setLoading(false);
    }
  };

  // Cleanup object URL on unmount
  useEffect(() => {
    return () => {
      if (imageUrl) {
        URL.revokeObjectURL(imageUrl);
      }
    };
  }, [imageUrl]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32 bg-slate-100 rounded-lg">
        <Loader2 className="w-6 h-6 text-slate-400 animate-spin" />
      </div>
    );
  }

  if (unavailable) {
    return (
      <div
        className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 text-xs"
        data-testid={`image-unavailable-${attachmentId}`}
      >
        <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
        <span>Este archivo no está disponible (fue subido antes de abril 2026 y no se pudo migrar). Puedes volver a subirlo si aún lo tienes.</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-32 bg-slate-100 rounded-lg text-slate-400 text-sm">
        Error al cargar imagen
      </div>
    );
  }

  return (
    <>
      {/* Thumbnail */}
      <div 
        className="relative cursor-pointer group"
        onClick={() => setIsModalOpen(true)}
        data-testid={`image-preview-${attachmentId}`}
      >
        <img
          src={imageUrl}
          alt={fileName}
          className="max-h-48 w-auto rounded-lg border border-slate-200 object-contain"
        />
        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors rounded-lg flex items-center justify-center">
          <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
        </div>
      </div>

      {/* Full-size Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden">
          <DialogHeader className="px-4 py-3 border-b">
            <DialogTitle className="text-sm font-medium truncate pr-8">{fileName}</DialogTitle>
          </DialogHeader>
          <div className="overflow-auto p-4 flex items-center justify-center bg-slate-50" style={{ maxHeight: 'calc(90vh - 60px)' }}>
            <img
              src={imageUrl}
              alt={fileName}
              className="max-w-full h-auto object-contain"
              style={{ maxHeight: 'calc(90vh - 100px)' }}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

const TicketDetail = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { isAdmin, user } = useAuth();
  const { canWrite: canWritePerm, canAccess: canAccessPerm, isHelpdeskAdmin } = usePermissions();
  const canWriteTickets = isAdmin() || canWritePerm('mesa_de_ayuda', 'dashboard');
  
  const [loading, setLoading] = useState(true);
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [history, setHistory] = useState([]);
  const [agents, setAgents] = useState([]);
  const [categories, setCategories] = useState([]);
  const [userRole, setUserRole] = useState('helpdesk_requester');
  const [attachments, setAttachments] = useState([]);
  const [statusTransitions, setStatusTransitions] = useState({});
  
  // New comment
  const [newComment, setNewComment] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [sendingComment, setSendingComment] = useState(false);
  
  // Updates
  const [updating, setUpdating] = useState(false);
  
  // Close ticket dialog
  const [isCloseDialogOpen, setIsCloseDialogOpen] = useState(false);
  const [resolutionComment, setResolutionComment] = useState('');

  // CSAT (resolution feedback)
  const [csatComment, setCsatComment] = useState('');
  const [submittingCsat, setSubmittingCsat] = useState(false);

  const handleRateCsat = async (resolved) => {
    setSubmittingCsat(true);
    try {
      await helpdeskTicketsAPI.rateCsat(ticketId, {
        resolved,
        comment: csatComment.trim() || null,
      });
      if (resolved) {
        toast.success('Gracias por tu calificación.');
      } else {
        toast.success('Registramos tu respuesta. El ticket fue reabierto.');
      }
      setCsatComment('');
      await fetchTicketData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar la calificación');
    } finally {
      setSubmittingCsat(false);
    }
  };

  useEffect(() => {
    fetchTicketData();
  }, [ticketId]);

  const fetchTicketData = async () => {
    setLoading(true);
    try {
      const [ticketRes, commentsRes, roleRes, attachmentsRes, transitionsRes] = await Promise.all([
        helpdeskTicketsAPI.getById(ticketId),
        helpdeskTicketsAPI.getComments(ticketId),
        helpdeskAgentsAPI.getUserRole(),
        helpdeskTicketsAPI.getAttachments(ticketId),
        helpdeskConfigAPI.getStatusTransitions(),
      ]);
      
      setTicket(ticketRes.data);
      setComments(commentsRes.data);
      setUserRole(roleRes.data.role);
      setAttachments(attachmentsRes.data || []);
      setStatusTransitions(transitionsRes.data || {});
      
      // Get history, agents and categories if not requester
      if (roleRes.data.role !== 'helpdesk_requester') {
        const [historyRes, agentsRes, categoriesRes] = await Promise.all([
          helpdeskTicketsAPI.getHistory(ticketId),
          helpdeskAgentsAPI.getAll(),
          helpdeskCategoriesAPI.getAll(),
        ]);
        setHistory(historyRes.data);
        setAgents(agentsRes.data);
        setCategories((categoriesRes.data || []).filter(c => c.is_active));
      }
    } catch (error) {
      console.error('Error fetching ticket:', error);
      if (error.response?.status === 403) {
        toast.error('No tienes acceso a este ticket');
        navigate('/helpdesk');
      } else if (error.response?.status === 404) {
        toast.error('Ticket no encontrado');
        navigate('/helpdesk');
      } else {
        toast.error('Error al cargar el ticket');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    
    if (!newComment.trim()) {
      toast.error('El comentario no puede estar vacío');
      return;
    }
    
    setSendingComment(true);
    try {
      await helpdeskTicketsAPI.addComment(ticketId, {
        text: newComment.trim(),
        is_internal: isInternal,
      });
      toast.success('Comentario agregado');
      setNewComment('');
      setIsInternal(false);
      
      // Refresh comments
      const commentsRes = await helpdeskTicketsAPI.getComments(ticketId);
      setComments(commentsRes.data);
    } catch (error) {
      console.error('Error adding comment:', error);
      toast.error('Error al agregar el comentario');
    } finally {
      setSendingComment(false);
    }
  };

  // Attachment helpers
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return ImageIcon;
    if (mimeType?.includes('pdf')) return FileText;
    return File;
  };

  const handleDownloadAttachment = async (attachment) => {
    try {
      const response = await helpdeskAttachmentsAPI.download(attachment.id);
      const blob = new Blob([response.data], { type: attachment.mime_type });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = attachment.original_name;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      if (error?.response?.status === 410) {
        toast.error('Este archivo no está disponible (fue subido antes de abril 2026 y no se pudo migrar). Puedes volver a subirlo si aún lo tienes.', { duration: 8000 });
      } else {
        toast.error('Error al descargar el archivo');
      }
    }
  };

  const handleUpdateTicket = async (field, value) => {
    // If closing ticket, show dialog for resolution comment
    if (field === 'status' && value === 'closed') {
      setIsCloseDialogOpen(true);
      return;
    }
    
    // Client-side validation for status transitions
    if (field === 'status' && ticket) {
      const currentStatus = ticket.status;
      const allowedTransitions = statusTransitions[currentStatus] || [];
      
      if (!allowedTransitions.includes(value)) {
        const allowedLabels = allowedTransitions.map(s => STATUS_CONFIG[s]?.label || s).join(', ');
        toast.error(`Transición no permitida. Desde "${STATUS_CONFIG[currentStatus]?.label}" solo puedes ir a: ${allowedLabels || 'ninguno'}`);
        return;
      }
    }
    
    setUpdating(true);
    try {
      await helpdeskTicketsAPI.update(ticketId, { [field]: value });
      toast.success('Ticket actualizado');
      fetchTicketData();
    } catch (error) {
      console.error('Error updating ticket:', error);
      toast.error(error.response?.data?.detail || 'Error al actualizar el ticket');
    } finally {
      setUpdating(false);
    }
  };
  
  // Get allowed status options based on current status
  const getAllowedStatusOptions = () => {
    if (!ticket) return [];
    const currentStatus = ticket.status;
    const allowedTransitions = statusTransitions[currentStatus] || [];
    
    // Always include current status and allowed transitions
    return Object.entries(STATUS_CONFIG)
      .filter(([key]) => key === currentStatus || allowedTransitions.includes(key))
      .map(([key, config]) => ({
        value: key,
        label: config.label,
        disabled: key !== currentStatus && !allowedTransitions.includes(key),
        isCurrent: key === currentStatus,
      }));
  };
  
  // Check if commitment_date can be edited (only when status is 'open')
  const canEditCommitmentDate = () => {
    return ticket?.status === 'open' && isHelpdeskAdmin;
  };

  const handleCloseTicket = async () => {
    setUpdating(true);
    try {
      await helpdeskTicketsAPI.update(ticketId, {
        status: 'closed',
        resolution_comment: resolutionComment.trim() || null
      });
      toast.success('Ticket cerrado correctamente');
      setIsCloseDialogOpen(false);
      setResolutionComment('');
      fetchTicketData();
    } catch (error) {
      console.error('Error closing ticket:', error);
      toast.error(error.response?.data?.detail || 'Error al cerrar el ticket');
    } finally {
      setUpdating(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <div className="grid grid-cols-3 gap-6">
            <div className="col-span-2">
              <Skeleton className="h-96" />
            </div>
            <Skeleton className="h-96" />
          </div>
        </div>
      </Layout>
    );
  }

  if (!ticket) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">Ticket no encontrado</p>
        </div>
      </Layout>
    );
  }

  const statusConfig = STATUS_CONFIG[ticket.status] || STATUS_CONFIG.open;
  const StatusIcon = statusConfig.icon;
  const priorityConfig = PRIORITY_CONFIG[ticket.priority] || PRIORITY_CONFIG.medium;
  const canEdit = userRole !== 'helpdesk_requester' && canWriteTickets;
  const canAssign = userRole === 'helpdesk_admin' && canWriteTickets;
  const isClosed = ticket.status === 'closed';
  const csat = ticket.csat || null;
  const isRequesterOwner = userRole === 'helpdesk_requester' && ticket.requester_id === user?.id;
  const showCsatBanner = isClosed && csat?.status === 'pending' && isRequesterOwner;
  const showCsatResult = !!(csat && csat.status && csat.status !== 'pending');
  const CSAT_RESULT = {
    resolved: { icon: '✅', text: 'Solicitante confirmó que el problema fue resuelto', cls: 'bg-emerald-50 border-emerald-200 text-emerald-800' },
    unresolved: { icon: '❌', text: 'Solicitante reportó que el problema no fue resuelto', cls: 'bg-red-50 border-red-200 text-red-800' },
    assumed_resolved: { icon: '⏱', text: 'Sin respuesta del solicitante — resuelto por vencimiento de plazo', cls: 'bg-slate-50 border-slate-200 text-slate-700' },
  };

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate('/helpdesk')}>
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-mono text-slate-400">#{ticket.folio}</span>
              <Badge className={cn('text-xs', statusConfig.color)}>
                <StatusIcon className="w-3 h-3 mr-1" />
                {statusConfig.label}
              </Badge>
              <Badge className={cn('text-xs', priorityConfig.color)}>
                {priorityConfig.label}
              </Badge>
            </div>
            <h1 className="text-xl font-bold font-['Manrope'] text-slate-800">
              {ticket.title}
            </h1>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Description */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-slate-500">Descripción</CardTitle>
              </CardHeader>
              <CardContent>
                <div
                  className="text-slate-700 whitespace-pre-wrap prose prose-sm max-w-none"
                  data-testid="ticket-description-content"
                  dangerouslySetInnerHTML={{ __html: sanitizeHTML(ticket.description || '') }}
                />
              </CardContent>
            </Card>

            {/* CSAT feedback banner (requester only, closed + pending) */}
            {showCsatBanner && (
              <Card className="border-2 border-blue-300 bg-blue-50/50" data-testid="csat-banner">
                <CardContent className="p-5">
                  <h3 className="text-base font-semibold text-slate-800 mb-3">¿Se resolvió tu problema?</h3>
                  <Textarea
                    value={csatComment}
                    onChange={(e) => setCsatComment(e.target.value.slice(0, 300))}
                    placeholder="Comentario (opcional)"
                    maxLength={300}
                    rows={2}
                    className="mb-1 bg-white"
                    data-testid="csat-comment-input"
                  />
                  <div className="text-right text-xs text-slate-400 mb-3">{csatComment.length}/300</div>
                  <div className="flex flex-wrap items-center gap-3">
                    <Button
                      type="button"
                      onClick={() => handleRateCsat(true)}
                      disabled={submittingCsat}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="csat-resolved-btn"
                    >
                      <ThumbsUp className="w-4 h-4 mr-2" /> Sí, resuelto
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleRateCsat(false)}
                      disabled={submittingCsat}
                      className="border-red-300 text-red-700 hover:bg-red-50"
                      data-testid="csat-unresolved-btn"
                    >
                      <ThumbsDown className="w-4 h-4 mr-2" /> No, sigue pendiente
                    </Button>
                    {submittingCsat && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* CSAT result (all roles, read-only) */}
            {showCsatResult && (
              <Card data-testid="csat-result">
                <CardContent className="p-4">
                  <div className={cn('rounded-lg border px-4 py-3', CSAT_RESULT[csat.status]?.cls)}>
                    <p className="text-sm font-medium">
                      <span className="mr-2">{CSAT_RESULT[csat.status]?.icon}</span>
                      {CSAT_RESULT[csat.status]?.text}
                    </p>
                    {csat.comment && (
                      <p className="text-sm mt-2 italic opacity-90">"{csat.comment}"</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Attachments with Image Preview */}
            {attachments.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                    <Paperclip className="w-4 h-4" />
                    Evidencia ({attachments.length} archivo{attachments.length > 1 ? 's' : ''})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {attachments.map(attachment => {
                      const FileIcon = getFileIcon(attachment.mime_type);
                      const isImage = attachment.mime_type?.startsWith('image/');
                      
                      return (
                        <div
                          key={attachment.id}
                          className="bg-slate-50 rounded-lg p-3 hover:bg-slate-100 transition-colors"
                          data-testid={`attachment-${attachment.id}`}
                        >
                          {/* Image Preview */}
                          {isImage && (
                            <div className="mb-3">
                              <ImagePreview 
                                attachmentId={attachment.id}
                                fileName={attachment.original_name}
                                mimeType={attachment.mime_type}
                              />
                            </div>
                          )}
                          
                          {/* File Info & Download */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 min-w-0">
                              <FileIcon className="w-5 h-5 text-slate-400 flex-shrink-0" />
                              <div className="min-w-0">
                                <p className="text-sm font-medium text-slate-700 truncate">
                                  {attachment.original_name}
                                </p>
                                <p className="text-xs text-slate-400">
                                  {formatFileSize(attachment.size_bytes)} • Subido por {attachment.uploaded_by_name}
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadAttachment(attachment)}
                              className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              data-testid={`download-${attachment.id}`}
                            >
                              <Download className="w-4 h-4 mr-1" />
                              Descargar
                            </Button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Comments */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                  <MessageSquare className="w-4 h-4" />
                  Comentarios ({comments.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {comments.length === 0 ? (
                  <p className="text-sm text-slate-400 text-center py-4">
                    No hay comentarios aún
                  </p>
                ) : (
                  <ScrollArea className="max-h-[400px] pr-4">
                    <div className="space-y-4">
                      {comments.map(comment => (
                        <div
                          key={comment.id}
                          className={cn(
                            "p-3 rounded-lg",
                            comment.is_internal 
                              ? "bg-amber-50 border border-amber-200" 
                              : "bg-slate-50 border border-slate-200"
                          )}
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-400" />
                              <span className="text-sm font-medium">{comment.created_by_name}</span>
                              {comment.is_internal && (
                                <Badge variant="outline" className="text-xs text-amber-600 border-amber-300">
                                  <Lock className="w-3 h-3 mr-1" />
                                  Interno
                                </Badge>
                              )}
                            </div>
                            <span className="text-xs text-slate-400">
                              {formatDate(comment.created_at.split('T')[0])}
                            </span>
                          </div>
                          <p className="text-sm text-slate-700 whitespace-pre-wrap">
                            {comment.text}
                          </p>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                )}

                {/* Add Comment Form — Fase 3: basic users can comment on their own tickets */}
                {!isClosed && (canWriteTickets || (canAccessPerm('mesa_de_ayuda', 'dashboard') && ticket?.requester_id === user?.id)) && (
                  <form onSubmit={handleAddComment} className="space-y-3 pt-4 border-t">
                    <Textarea
                      placeholder="Escribe un comentario..."
                      value={newComment}
                      onChange={(e) => setNewComment(e.target.value)}
                      className="min-h-[80px]"
                      data-testid="comment-input"
                    />
                    <div className="flex items-center justify-between">
                      {canEdit && (
                        <div className="flex items-center gap-2">
                          <Checkbox
                            id="internal"
                            checked={isInternal}
                            onCheckedChange={setIsInternal}
                          />
                          <Label htmlFor="internal" className="text-sm text-slate-500 cursor-pointer">
                            Comentario interno (solo visible para agentes)
                          </Label>
                        </div>
                      )}
                      <Button type="submit" disabled={sendingComment} size="sm" data-testid="send-comment-btn">
                        <Send className="w-4 h-4 mr-2" />
                        {sendingComment ? 'Enviando...' : 'Enviar'}
                      </Button>
                    </div>
                  </form>
                )}

                {isClosed && (
                  <div className="text-center py-4 text-sm text-slate-400 border-t">
                    Este ticket está cerrado. No se pueden agregar más comentarios.
                  </div>
                )}
              </CardContent>
            </Card>

            {/* History (Agent/Admin only) */}
            {canEdit && history.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-slate-500 flex items-center gap-2">
                    <History className="w-4 h-4" />
                    Historial de cambios
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {history.map(item => item.field === 'Agente asignado post-cierre' ? (
                      <div key={item.id} className="flex items-start gap-3 text-sm" data-testid={`history-post-close-${item.id}`}>
                        <div className="w-2 h-2 rounded-full bg-blue-300 mt-1.5 flex-shrink-0" />
                        <div className="flex-1 text-slate-600">
                          <span className="font-medium text-slate-800">Agente asignado post-cierre:</span>{' '}
                          {item.old_value || 'Sin asignar'} → <span className="text-emerald-600">{item.new_value}</span>{' '}
                          por <span className="font-medium">{item.changed_by_name}</span> el{' '}
                          {new Date(item.changed_at).toLocaleString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false })}
                        </div>
                      </div>
                    ) : (
                      <div key={item.id} className="flex items-start gap-3 text-sm">
                        <div className="w-2 h-2 rounded-full bg-slate-300 mt-1.5 flex-shrink-0" />
                        <div className="flex-1">
                          <span className="font-medium">{item.changed_by_name}</span>
                          <span className="text-slate-500"> cambió </span>
                          <span className="font-medium">{item.field}</span>
                          <span className="text-slate-500"> de </span>
                          <span className="text-rose-600 line-through">{item.old_value || 'vacío'}</span>
                          <span className="text-slate-500"> a </span>
                          <span className="text-emerald-600">{item.new_value}</span>
                        </div>
                        <span className="text-xs text-slate-400 flex-shrink-0 whitespace-nowrap">
                          {new Date(item.changed_at).toLocaleDateString('es-MX', { day: '2-digit', month: 'short' })}{' '}
                          {new Date(item.changed_at).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit', hour12: false })}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Ticket Info */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-slate-500">Información</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-3">
                  <div className="flex items-center gap-2 text-sm">
                    <User className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Solicitante:</span>
                    <span className="font-medium">{ticket.requester_name}</span>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Tag className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Categoría:</span>
                    <span className="font-medium">{ticket.category_name || 'N/A'}</span>
                  </div>
                  {/* Área - Single unified field (replaced Department and Classification) */}
                  <div className="flex items-center gap-2 text-sm">
                    <Building className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Área:</span>
                    <Badge 
                      variant="outline" 
                      className={cn("text-xs", getAreaColor(ticket.area))}
                    >
                      {normalizeArea(ticket.area) || 'N/A'}
                    </Badge>
                  </div>
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span className="text-slate-500">Creado:</span>
                    <span className="font-medium" data-testid="ticket-created-at">{formatDateTimeMX(ticket.created_at)}</span>
                  </div>
                  {ticket.requested_date && (
                    <div className="flex items-center gap-2 text-sm">
                      <Clock className="w-4 h-4 text-amber-500" />
                      <span className="text-slate-500">Fecha solicitada:</span>
                      <span className="font-medium text-amber-600">
                        {new Date(ticket.requested_date + 'T00:00:00').toLocaleDateString('es-MX', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                  {/* Commitment Date (Admin's date) */}
                  {ticket.commitment_date && (
                    <div className="flex items-center gap-2 text-sm">
                      <CalendarClock className="w-4 h-4 text-emerald-500" />
                      <span className="text-slate-500">Fecha compromiso:</span>
                      <span className="font-medium text-emerald-600">
                        {new Date(ticket.commitment_date + 'T00:00:00').toLocaleDateString('es-MX', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </span>
                    </div>
                  )}
                  {ticket.assigned_to_name && (
                    <div className="flex items-center gap-2 text-sm">
                      <UserCheck className="w-4 h-4 text-blue-500" />
                      <span className="text-slate-500">Asignado a:</span>
                      <span className="font-medium text-blue-600">{ticket.assigned_to_name}</span>
                    </div>
                  )}
                  {ticket.sla_due_date && ticket.status !== 'closed' && (
                    <div className="flex items-center gap-2 text-sm flex-wrap" data-testid="sla-due-date">
                      {(ticket.status === 'on_hold' || ticket.status === 'scheduled') ? (
                        <Pause className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      ) : new Date(ticket.sla_due_date) < new Date() ? (
                        <AlertTriangle className="w-4 h-4 text-rose-500 flex-shrink-0" />
                      ) : (
                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                      )}
                      <span className="text-slate-500">Vencimiento SLA:</span>
                      <span className={cn(
                        "font-medium",
                        (ticket.status === 'on_hold' || ticket.status === 'scheduled')
                          ? "text-slate-500"
                          : new Date(ticket.sla_due_date) < new Date() ? "text-rose-600" : "text-emerald-600"
                      )}>
                        {new Date(ticket.sla_due_date).toLocaleDateString('es-MX', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric'
                        })}{' '}
                        {new Date(ticket.sla_due_date).toLocaleTimeString('es-MX', {
                          hour: '2-digit',
                          minute: '2-digit',
                          hour12: false
                        })}
                      </span>
                      {(ticket.status === 'on_hold' || ticket.status === 'scheduled') && (
                        <span className="inline-flex items-center text-[10px] font-medium px-2 py-0.5 rounded-full bg-[#FEF9C3] text-amber-700" data-testid="sla-paused-badge">
                          SLA pausado
                        </span>
                      )}
                    </div>
                  )}
                </div>

                {/* Resolution Comment */}
                {ticket.status === 'closed' && ticket.resolution_comment && (
                  <div className="mt-4 pt-4 border-t border-slate-200">
                    <div className="text-sm font-medium text-emerald-700 mb-2 flex items-center gap-2">
                      <CheckCircle2 className="w-4 h-4" />
                      Resolución
                    </div>
                    <p className="text-sm text-slate-600 bg-emerald-50 p-3 rounded-lg whitespace-pre-wrap">
                      {ticket.resolution_comment}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Reasignación post-cierre (solo Admin) */}
            {isClosed && isAdmin() && (
              <Card data-testid="closed-reassign-card">
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-slate-500">Asignado a (post-cierre)</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  <Select
                    value={ticket.assigned_to || 'unassigned'}
                    onValueChange={(v) => handleUpdateTicket('assigned_to', v === 'unassigned' ? null : v)}
                    disabled={updating}
                  >
                    <SelectTrigger data-testid="closed-assign-select">
                      <SelectValue placeholder="Sin asignar" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Sin asignar</SelectItem>
                      {agents.map(agent => (
                        <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-400">
                    Solo cambia el agente. El ticket sigue Cerrado y no se envían notificaciones.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Actions (Agent/Admin) */}
            {canEdit && !isClosed && (
              <Card>
                <CardHeader>
                  <CardTitle className="text-sm font-medium text-slate-500">Acciones</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Status with transition validation */}
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500">Estado</Label>
                    <TooltipProvider>
                      <Select
                        value={ticket.status}
                        onValueChange={(v) => handleUpdateTicket('status', v)}
                        disabled={updating}
                      >
                        <SelectTrigger data-testid="status-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {getAllowedStatusOptions().map(option => (
                            <Tooltip key={option.value}>
                              <TooltipTrigger asChild>
                                <SelectItem 
                                  value={option.value}
                                  disabled={option.disabled}
                                  className={cn(option.isCurrent && "font-semibold")}
                                >
                                  {option.label}
                                  {option.isCurrent && " (actual)"}
                                </SelectItem>
                              </TooltipTrigger>
                              {option.disabled && (
                                <TooltipContent side="left">
                                  <p>Transición no permitida</p>
                                </TooltipContent>
                              )}
                            </Tooltip>
                          ))}
                        </SelectContent>
                      </Select>
                    </TooltipProvider>
                    <p className="text-xs text-slate-400">
                      Flujo: Abierto → En proceso → En espera/Programado → Cerrado
                    </p>
                  </div>

                  {/* Priority */}
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500">Prioridad</Label>
                    <Select
                      value={ticket.priority}
                      onValueChange={(v) => handleUpdateTicket('priority', v)}
                      disabled={updating}
                    >
                      <SelectTrigger data-testid="priority-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="low">Baja</SelectItem>
                        <SelectItem value="medium">Media</SelectItem>
                        <SelectItem value="high">Alta</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Category */}
                  {categories.length > 0 && (
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-500 flex items-center gap-2">
                        <Tag className="w-3 h-3" />
                        Categoría
                      </Label>
                      <Select
                        value={ticket.category_id}
                        onValueChange={(v) => handleUpdateTicket('category_id', v)}
                        disabled={updating}
                      >
                        <SelectTrigger data-testid="category-select">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {categories.map(cat => (
                            <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-400">
                        Recategoriza si la solicitud fue clasificada incorrectamente
                      </p>
                    </div>
                  )}

                  {/* Área - editable field for reclassification */}
                  <div className="space-y-2">
                    <Label className="text-xs text-slate-500 flex items-center gap-2">
                      <Building className="w-3 h-3" />
                      Área
                    </Label>
                    <Select
                      value={normalizeArea(ticket.area) || ''}
                      onValueChange={(v) => handleUpdateTicket('area', v)}
                      disabled={updating}
                    >
                      <SelectTrigger data-testid="area-select">
                        <SelectValue placeholder="Selecciona área" />
                      </SelectTrigger>
                      <SelectContent>
                        {AREAS.map(({ value, label }) => (
                          <SelectItem key={value} value={value}>{label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-400">
                      Reclasifica si el diagnóstico lo requiere
                    </p>
                  </div>

                  {/* Assignment (Admin only) */}
                  {canAssign && (
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-500">Asignar a (Equipo TI)</Label>
                      <Select
                        value={ticket.assigned_to || 'unassigned'}
                        onValueChange={(v) => handleUpdateTicket('assigned_to', v === 'unassigned' ? null : v)}
                        disabled={updating}
                      >
                        <SelectTrigger data-testid="assign-select">
                          <SelectValue placeholder="Sin asignar" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="unassigned">Sin asignar</SelectItem>
                          {agents.length === 0 ? (
                            <SelectItem value="no-agents" disabled>
                              No hay agentes configurados
                            </SelectItem>
                          ) : (
                            agents.map(agent => (
                              <SelectItem key={agent.id} value={agent.id}>
                                {agent.name}
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                      {agents.length === 0 && (
                        <p className="text-xs text-amber-600">
                          Configura el Equipo TI en Ajustes → Mesa de Ayuda
                        </p>
                      )}
                    </div>
                  )}
                  
                  {/* Commitment Date (Admin only, only when status is 'open') */}
                  {canAssign && (
                    <div className="space-y-2">
                      <Label className="text-xs text-slate-500 flex items-center gap-2">
                        Fecha compromiso
                        {!canEditCommitmentDate() && ticket.status !== 'open' && (
                          <Badge variant="outline" className="text-xs">
                            <Lock className="w-3 h-3 mr-1" />
                            Bloqueado
                          </Badge>
                        )}
                      </Label>
                      <Input
                        type="date"
                        value={ticket.commitment_date || ''}
                        onChange={(e) => handleUpdateTicket('commitment_date', e.target.value)}
                        disabled={updating || !canEditCommitmentDate()}
                        min={ticket.created_at?.split('T')[0]}
                        className={cn(!canEditCommitmentDate() && "bg-slate-100 cursor-not-allowed")}
                        data-testid="commitment-date-input"
                      />
                      {!canEditCommitmentDate() && ticket.status !== 'open' && (
                        <p className="text-xs text-slate-400">
                          Solo se puede modificar cuando el ticket está Abierto
                        </p>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Timestamps */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm font-medium text-slate-500">Tiempos</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-slate-500">Creado:</span>
                  <span data-testid="ticket-times-created-at">{formatDateTimeMX(ticket.created_at)}</span>
                </div>
                {ticket.first_response_at && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Primera respuesta:</span>
                    <span>{formatDate(ticket.first_response_at.split('T')[0])}</span>
                  </div>
                )}
                {ticket.closed_at && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Cerrado:</span>
                    <span>{formatDate(ticket.closed_at.split('T')[0])}</span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Close Ticket Dialog */}
      <Dialog open={isCloseDialogOpen} onOpenChange={setIsCloseDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              Cerrar Ticket
            </DialogTitle>
            <DialogDescription>
              Agrega un comentario de resolución para informar al solicitante sobre la solución.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              <div className="font-medium text-slate-700">#{ticket?.folio}</div>
              <div className="text-slate-500">{ticket?.title}</div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="resolution">Comentario de resolución (opcional)</Label>
              <Textarea
                id="resolution"
                placeholder="Describe cómo se resolvió el problema o qué acción se tomó..."
                value={resolutionComment}
                onChange={(e) => setResolutionComment(e.target.value)}
                className="min-h-[120px]"
                data-testid="resolution-comment-input"
              />
              <p className="text-xs text-slate-500">
                Este comentario se enviará por correo al solicitante y quedará registrado en el ticket.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => {
                setIsCloseDialogOpen(false);
                setResolutionComment('');
              }}
            >
              Cancelar
            </Button>
            <Button 
              onClick={handleCloseTicket} 
              disabled={updating}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-close-btn"
            >
              {updating ? 'Cerrando...' : 'Cerrar Ticket'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default TicketDetail;
